import FormResponseView from '@/components/forms/FormResponseView'
import { ClientCheckinCompareLoadingState } from '@/components/client/ClientTabLoadingStates'
import MetricChartDialog from '@/components/metricsComponents/MetricChartDialog'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Json } from '@/lib/supabase/supabaseTypes'
import { cn } from '@/lib/utils/ui'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import { createFileRoute } from '@tanstack/react-router'
import { Check } from 'lucide-react'

import * as luxon from 'luxon'
import React, { useEffect, useMemo, useState } from 'react'
import { FunctionComponent } from 'react'
import { FaArrowLeftLong } from 'react-icons/fa6'

interface FormResponseItem {
  q_num: number
  title: string
  type: string
  data: Json
}

interface ClientFormResponse {
  id: string
  response_date: string
  form_name: string
  responses: FormResponseItem[]
  coach_res?: {
    notes: string | null
  }[]
}

type CompareSearch = {
  date?: string
}

export const Route = createFileRoute(
  '/_auth/_dashboard/clients/$clientId/_layout/checkin/compare',
)({
  component: ComparePage,
  loader: ({ params }) =>
    trpcUtils.checkins.listDatesForClient.ensureData({
      clientId: params.clientId,
    }),
  pendingComponent: ClientCheckinCompareLoadingState,
  validateSearch: (search: CompareSearch) => {
    return { date: search.date }
  },
})

function ComparePage() {
  const { clientId } = Route.useParams()
  const { date } = Route.useSearch()

  const [leftFormDate, setLeftFormDate] = useState<string | undefined>(date)
  const [rightFormDate, setRightFormDate] = useState<string | undefined>()

  const [metricDialogId, setMetricDialogId] = useState<string>()

  const { data: dateRows } = trpc.checkins.listDatesForClient.useQuery({
    clientId,
  })
  const secondDate = useMemo(() => {
    if (!date) return undefined
    return (dateRows ?? []).find(
      (row) => row.type === 'check-in' && row.response_date < date,
    )?.response_date
  }, [date, dateRows])

  useEffect(() => {
    if (secondDate === undefined) return
    setRightFormDate((current) => current ?? secondDate)
  }, [secondDate])
  const { data: formOptions } = trpc.forms.listOptions.useQuery({
    type: 'check-in',
  })
  const checkInDates = useMemo(
    () => (dateRows ?? []).filter((row) => row.type === 'check-in'),
    [dateRows],
  )
  const formNameById = useMemo(
    () =>
      Object.fromEntries(
        (formOptions ?? []).map((form) => [form.id, form.name]),
      ),
    [formOptions],
  )

  const leftMeta = checkInDates.find(
    (row) => row.response_date === leftFormDate,
  )
  const rightMeta = checkInDates.find(
    (row) => row.response_date === rightFormDate,
  )

  const { data: leftFormData } = trpc.checkins.getByDate.useQuery(
    {
      clientId,
      formId: leftMeta?.form_id ?? '',
      responseDate: leftFormDate ?? '',
    },
    { enabled: Boolean(leftMeta && leftFormDate) },
  )
  const { data: rightFormData } = trpc.checkins.getByDate.useQuery(
    {
      clientId,
      formId: rightMeta?.form_id ?? '',
      responseDate: rightFormDate ?? '',
    },
    { enabled: Boolean(rightMeta && rightFormDate) },
  )

  const leftForm = useMemo(
    () => mapCompareResponse(leftFormData),
    [leftFormData],
  )
  const rightForm = useMemo(
    () => mapCompareResponse(rightFormData),
    [rightFormData],
  )

  const handleGoback = () => {
    window.history.back()
  }

  return (
    <>
      <MetricChartDialog
        open={metricDialogId !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setMetricDialogId(undefined)
          }
        }}
        metricId={metricDialogId}
        clientId={clientId}
      />
      <div className="ph-no-capture w-full md:w-[90%] mx-auto">
        <div className="min-w-[30%] h-full mb-10 p-4">
          <div className="flex justify-between flex-row mb-3">
            <div className="flex gap-4 items-center">
              <Button variant={'ghost'} onClick={handleGoback}>
                <FaArrowLeftLong />
                <h2 className="text-lg font-medium ml-4">Comparison</h2>
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto pb-2">
            <div className="flex min-w-full w-max flex-row justify-center gap-5">
              <FormResponse
                form={leftForm}
                clientId={clientId}
                dates={checkInDates}
                formNameById={formNameById}
                onFormChanged={setLeftFormDate}
                otherFormDate={rightFormDate}
                setMetricDialogId={setMetricDialogId}
              />
              <FormResponse
                form={rightForm}
                clientId={clientId}
                dates={checkInDates}
                formNameById={formNameById}
                onFormChanged={setRightFormDate}
                otherFormDate={leftFormDate}
                setMetricDialogId={setMetricDialogId}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function mapCompareResponse(
  data:
    | {
        id: string
        response_date: string
        form_name: string | null
        form: { name: string | null }
        responses: {
          q_num: number
          title: string
          type: string
          data?: unknown
        }[]
      }
    | null
    | undefined,
): ClientFormResponse | undefined {
  if (!data) return undefined
  return {
    id: data.id,
    response_date: data.response_date,
    form_name: data.form_name ?? data.form.name ?? '',
    responses: data.responses.map((response) => ({
      q_num: response.q_num,
      title: response.title,
      type: response.type,
      data: (response.data ?? null) as Json,
    })),
  }
}

interface FormResponseProps {
  form?: ClientFormResponse | null
  clientId: string
  dates: { id: string; response_date: string; form_id: string; type: string }[]
  formNameById: Record<string, string>
  onFormChanged: (date: string) => void
  otherFormDate?: string
  setMetricDialogId: (metricId: string) => void
}

const FormResponse: FunctionComponent<FormResponseProps> = ({
  form,
  clientId,
  dates,
  formNameById,
  onFormChanged,
  otherFormDate,
  setMetricDialogId,
}) => {
  const coachReviewNotes = form?.coach_res?.[0]?.notes?.trim() ?? ''

  return (
    <Card className="flex w-[90vw] min-w-[360px] max-w-[760px] shrink-0 flex-col gap-4 p-4 sm:w-[560px]">
      <div className="my-3">
        <FormSelect
          dates={dates}
          formNameById={formNameById}
          currentForm={form}
          onFormSelected={onFormChanged}
          otherFormDate={otherFormDate}
        />
      </div>

      {coachReviewNotes ? (
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="mb-2 text-sm font-medium">Coach review notes</p>
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">
            {coachReviewNotes}
          </p>
        </div>
      ) : null}

      {form?.responses.map((response: FormResponseItem) => (
        <div key={response.q_num} className="flex gap-3 flex-col">
          <FormResponseView
            response={response}
            clientId={clientId}
            metricResponseSelected={(metricId) => setMetricDialogId(metricId)}
          />
        </div>
      ))}
    </Card>
  )
}

interface FormSelectProps {
  dates: { id: string; response_date: string; form_id: string; type: string }[]
  formNameById: Record<string, string>
  currentForm?: ClientFormResponse | null
  otherFormDate?: string
  onFormSelected: (date: string) => void
}

const FormSelect: FunctionComponent<FormSelectProps> = ({
  dates,
  formNameById,
  currentForm,
  onFormSelected,
  otherFormDate,
}) => {
  const [open, setOpen] = React.useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={'justify-between w-full'}
        >
          {currentForm
            ? `${currentForm.form_name} - ${stringFromDate(currentForm.response_date)}`
            : 'Select a check-in'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={'w-full p-0'}>
        <Command>
          {/* <CommandInput placeholder={`Search checkins...`} /> */}
          <CommandEmpty>{`No check-in's found`}</CommandEmpty>
          <CommandList className="max-h-[200px] overflow-y-scroll">
            <CommandGroup>
              {dates.map((row) => {
                const formName =
                  formNameById[row.form_id] ??
                  (row.response_date === currentForm?.response_date
                    ? currentForm.form_name
                    : 'Check-in')
                return (
                  <CommandItem
                    key={row.id}
                    value={row.response_date}
                    onSelect={() => {
                      setOpen(false)
                      onFormSelected(row.response_date)
                    }}
                    disabled={row.response_date === otherFormDate}
                  >
                    <Check
                      className={cn(
                        'w-4 h-4 mr-2',
                        row.response_date === currentForm?.response_date
                          ? 'opactiy-100'
                          : 'opacity-0',
                      )}
                    />
                    {formName} - {stringFromDate(row.response_date)}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function stringFromDate(date: string) {
  return luxon.DateTime.fromISO(date).toLocaleString(
    luxon.DateTime.DATETIME_MED,
  )
}
